/**
 * WS-NAV — ⌘/Ctrl + 0–6 workspace shortcuts.
 *
 * `0` is Home; `1`–`6` are the workspaces in canonical order. The handler is
 * deliberately conservative: it ignores repeats, ignores keystrokes typed into
 * a field (an editor shortcut must never steal a digit from a form), and only
 * claims a digit the caller actually has a workspace for — otherwise the
 * browser keeps its own tab-switch binding.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HOME_PATH } from "../workspaces/resolve.js";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";

/** `⌘0` on Apple platforms, `Ctrl+0` everywhere else. */
export function shortcutHint(digit: number): string {
  const isApple =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.userAgent);
  return isApple ? `⌘${String(digit)}` : `Ctrl+${String(digit)}`;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Installs the global shortcut listener. Mounted once at the app shell so the
 * shortcuts work on every route, including the ones where the sidebar is
 * hidden (mobile) or collapsed (analysis).
 */
export function useWorkspaceShortcuts(): void {
  const { visible, navigateToWorkspace } = useActiveWorkspace();
  const navigate = useNavigate();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey || event.repeat) return;
      if (isTypingTarget(event.target)) return;
      if (!/^[0-6]$/.test(event.key)) return;

      const digit = Number(event.key);
      if (digit === 0) {
        event.preventDefault();
        void navigate(HOME_PATH);
        return;
      }
      const target = visible.find((w) => w.shortcut === digit);
      if (!target) return;
      event.preventDefault();
      // Same rule as the switcher and the Home cards: a shortcut is an
      // explicit choice. Without carrying it, ⌘3 pressed while INSIDE Projects
      // would resolve `/data-acquisitions` back to Projects (which also lists
      // it) and the user could never leave by keyboard.
      navigateToWorkspace(target);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [visible, navigateToWorkspace, navigate]);
}
